import type { JsonValue } from "@e308/core";
import { DURATION_PRESETS, defaultMap, type MapId, topology } from "./content/setup.js";
import type { ActiveBorder, MatchLifecycleState, MatchOutcome } from "./lifecycle-types.js";
import type { ConflictConfig, DurationPresetId, LockedSeat, MatchMode } from "./types.js";

export const NEW_BORDER_PEACE_MS = 30_000;
export const OVERTIME_LIMIT_MS = 10 * 60_000;

export interface LockedMatchSetup {
  readonly mode: MatchMode;
  readonly mapId: MapId;
  readonly durationPreset: DurationPresetId;
  readonly timeLimitMs: number;
  readonly openingPeaceMs: number;
  readonly originalEdges: readonly (readonly [string, string])[];
  readonly teamBySeat: Readonly<Record<string, string>>;
}

export function lockMatchSetup(config: ConflictConfig): LockedMatchSetup {
  const mode = config.mode ?? "every-town-for-itself";
  const mapId = config.mapId ?? defaultMap(config.seats.length);
  const durationPreset = config.durationPreset ?? "standard-shift";
  const preset = DURATION_PRESETS.find((entry) => entry.id === durationPreset);
  if (!preset) throw new TypeError(`Unknown duration preset: ${durationPreset}`);
  const generated = topology(
    mapId,
    config.seats.map((seat) => seat.id),
  );
  const declared = edgesFromSeats(config.seats);
  if (!sameEdges(generated, declared))
    throw new TypeError(`Declared adjacency does not match the ${mapId} map`);
  const teamBySeat = Object.freeze(
    Object.fromEntries(
      config.seats.map((seat) => [
        seat.id,
        mode === "team-shift" ? (seat.teamId as string) : seat.id,
      ]),
    ),
  );
  return Object.freeze({
    mode,
    mapId,
    durationPreset,
    timeLimitMs: preset.limitMinutes * 60_000,
    openingPeaceMs: preset.openingPeaceMinutes * 60_000,
    originalEdges: generated,
    teamBySeat,
  });
}

export function initialLifecycle(setup: LockedMatchSetup): MatchLifecycleState & JsonValue {
  return cloneLifecycle({
    status: "active",
    mode: setup.mode,
    mapId: setup.mapId,
    durationPreset: setup.durationPreset,
    timeLimitMs: setup.timeLimitMs,
    openingPeaceUntilMs: setup.openingPeaceMs,
    overtimeStartedAtMs: null,
    endedAtMs: null,
    outcome: null,
    eliminated: [],
    borders: setup.originalEdges.map(([left, right]) => ({
      left,
      right,
      fightingStartsAtMs: setup.openingPeaceMs,
    })),
  }) as MatchLifecycleState & JsonValue;
}

export function contractedEdges(
  originalEdges: readonly (readonly [string, string])[],
  eliminated: ReadonlySet<string>,
): readonly (readonly [string, string])[] {
  const adjacency = new Map<string, Set<string>>();
  for (const [left, right] of originalEdges) {
    addNeighbor(adjacency, left, right);
    addNeighbor(adjacency, right, left);
  }
  const result = new Map<string, readonly [string, string]>();
  const addEdge = (left: string, right: string) => {
    const edge = [left, right].sort() as [string, string];
    result.set(`${edge[0]}:${edge[1]}`, edge);
  };
  for (const [left, right] of originalEdges) {
    if (!eliminated.has(left) && !eliminated.has(right)) addEdge(left, right);
  }
  const unseen = new Set(eliminated);
  while (unseen.size > 0) {
    const first = [...unseen].sort()[0] as string;
    const stack = [first];
    const boundary = new Set<string>();
    unseen.delete(first);
    while (stack.length > 0) {
      const current = stack.pop() as string;
      for (const neighbor of adjacency.get(current) ?? []) {
        if (!eliminated.has(neighbor)) boundary.add(neighbor);
        else if (unseen.delete(neighbor)) stack.push(neighbor);
      }
    }
    const survivors = [...boundary].sort();
    for (let left = 0; left < survivors.length; left += 1) {
      for (let right = left + 1; right < survivors.length; right += 1) {
        addEdge(survivors[left] as string, survivors[right] as string);
      }
    }
  }
  return Object.freeze([...result.values()].sort(compareEdges));
}

export function reconcileBorders(
  state: MatchLifecycleState,
  nextEdges: readonly (readonly [string, string])[],
  nowMs: number,
): { readonly state: MatchLifecycleState & JsonValue; readonly added: readonly ActiveBorder[] } {
  const current = new Map(
    state.borders.map((border) => [edgeKey(border.left, border.right), border]),
  );
  const added: ActiveBorder[] = [];
  const borders = nextEdges.map(([left, right]) => {
    const prior = current.get(edgeKey(left, right));
    if (prior) return prior;
    const border = Object.freeze({ left, right, fightingStartsAtMs: nowMs + NEW_BORDER_PEACE_MS });
    added.push(border);
    return border;
  });
  return {
    state: cloneLifecycle({ ...state, borders }) as MatchLifecycleState & JsonValue,
    added: Object.freeze(added),
  };
}

export function borderIsActive(state: MatchLifecycleState, left: string, right: string): boolean {
  return state.borders.some(
    (border) =>
      (border.left === left && border.right === right) ||
      (border.left === right && border.right === left),
  );
}

export function borderCanFight(
  state: MatchLifecycleState,
  left: string,
  right: string,
  nowMs: number,
): boolean {
  const border = state.borders.find(
    (entry) =>
      (entry.left === left && entry.right === right) ||
      (entry.left === right && entry.right === left),
  );
  return Boolean(border && nowMs >= border.fightingStartsAtMs);
}

export function outcomeFor(
  livingSeatIds: readonly string[],
  mode: MatchMode,
  teamBySeat: Readonly<Record<string, string>>,
): MatchOutcome | null {
  const sides = new Set(
    livingSeatIds.map((seatId) => (mode === "team-shift" ? teamBySeat[seatId] : seatId)),
  );
  if (sides.size > 1) return null;
  if (sides.size === 0) return Object.freeze({ kind: "draw", winnerId: null });
  const winnerId = [...sides][0] as string;
  return Object.freeze({
    kind: mode === "team-shift" ? "team-win" : "seat-win",
    winnerId,
  });
}

export function cloneLifecycle(state: MatchLifecycleState): MatchLifecycleState {
  return Object.freeze({
    ...state,
    outcome: state.outcome ? Object.freeze({ ...state.outcome }) : null,
    eliminated: Object.freeze(state.eliminated.map((entry) => Object.freeze({ ...entry }))),
    borders: Object.freeze(state.borders.map((border) => Object.freeze({ ...border }))),
  });
}

function edgesFromSeats(seats: readonly LockedSeat[]): readonly (readonly [string, string])[] {
  const edges = new Map<string, readonly [string, string]>();
  for (const seat of seats) {
    for (const neighbor of seat.adjacentTo) {
      const edge = [seat.id, neighbor].sort() as [string, string];
      edges.set(edgeKey(edge[0], edge[1]), edge);
    }
  }
  return [...edges.values()].sort(compareEdges);
}

function sameEdges(
  left: readonly (readonly [string, string])[],
  right: readonly (readonly [string, string])[],
): boolean {
  return (
    left.length === right.length &&
    left.every(
      (edge, index) => edgeKey(...edge) === edgeKey(...(right[index] as readonly [string, string])),
    )
  );
}

function addNeighbor(map: Map<string, Set<string>>, left: string, right: string): void {
  const neighbors = map.get(left) ?? new Set<string>();
  neighbors.add(right);
  map.set(left, neighbors);
}

function edgeKey(left: string, right: string): string {
  return left < right ? `${left}:${right}` : `${right}:${left}`;
}

function compareEdges(left: readonly string[], right: readonly string[]): number {
  return edgeKey(left[0] as string, left[1] as string).localeCompare(
    edgeKey(right[0] as string, right[1] as string),
  );
}
